// src/components/templates/diary/DiaryLearningView.jsx
// Learning Dashboard - Shows enrolled courses and progress
import React, { useState, useMemo } from 'react';
import {
  GraduationCap, Store, BookOpen, TrendingUp, Award, Search, Play,
  CheckCircle, Clock, ChevronRight, Target, Trophy, BarChart3, ArrowLeft
} from 'lucide-react';
import LearningMarketplace from './LearningMarketplace';
import EnrolledCourseViewer from './EnrolledCourseViewer';
import CompetencyReport from './CompetencyReport';

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  MARKETPLACE: 'MARKETPLACE',
  COURSE_VIEWER: 'COURSE_VIEWER',
  COMPETENCY_REPORT: 'COMPETENCY_REPORT'
};

const DiaryLearningView = ({ 
  bookId, 
  customStyling = {}, 
  user,
  isOwner = false,
  // Data from DiaryTemplate
  courses = [],           // All courses
  enrollments = [],       // User's enrollments
  // Handlers from DiaryTemplate
  onEnrollCourse,
  onUpdateEnrollment,
  onUnenrollCourse,
  onAddCourse,
  onUpdateCourse,
  onDeleteCourse,
  onSaveLesson,
  // Service
  diaryService
}) => {
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  // Calculate stats
  const stats = useMemo(() => {
    const completed = enrollments.filter(e => e.progress >= 100).length;
    const inProgress = enrollments.filter(e => e.progress > 0 && e.progress < 100).length;

    return {
      enrolled: enrollments.length,
      completed,
      inProgress,
      notStarted: enrollments.length - completed - inProgress
    };
  }, [enrollments]);

  // Filter enrolled courses
  const filteredEnrollments = useMemo(() => {
    if (!searchQuery) return enrollments;
    
    const q = searchQuery.toLowerCase();
    return enrollments.filter(enrollment => {
      const course = courses.find(c => c.id === enrollment.courseId);
      return (
        course?.title?.toLowerCase().includes(q) ||
        course?.description?.toLowerCase().includes(q) ||
        course?.instructor?.toLowerCase().includes(q)
      );
    });
  }, [enrollments, courses, searchQuery]);

  const handleCourseClick = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setViewState(ViewState.COURSE_VIEWER);
  };

  const handleViewReport = async (courseId) => {
    if (diaryService?.saveCompetencyReport && user) {
      const enrollment = enrollments.find(e => e.courseId === courseId);
      const course = courses.find(c => c.id === courseId);
      const lessons = await diaryService.getLessons(bookId, courseId);
      
      const report = {
        userId: user.uid,
        courseId,
        courseName: course?.title || '',
        overallProgress: enrollment?.progress || 0,
        completedLessons: enrollment?.completedLessons?.length || 0,
        totalLessons: lessons.length,
        competencyScores: enrollment?.competencyScores || {},
        assessmentResults: enrollment?.completedAssessments || {},
        totalTimeSpent: enrollment?.totalTimeSpent || 0
      };

      await diaryService.saveCompetencyReport(bookId, report);
    }
    
    setSelectedEnrollment({ courseId });
    setViewState(ViewState.COMPETENCY_REPORT);
  };

  // Marketplace View
  if (viewState === ViewState.MARKETPLACE) {
    return (
      <LearningMarketplace
        bookId={bookId}
        user={user}
        isOwner={isOwner}
        enrollments={enrollments}
        courses={courses}
        customStyling={customStyling}
        onEnroll={onEnrollCourse}
        onAddCourse={onAddCourse}
        onUpdateCourse={onUpdateCourse}
        onDeleteCourse={onDeleteCourse}
        onSaveLesson={onSaveLesson}
        onBack={() => setViewState(ViewState.DASHBOARD)}
        diaryService={diaryService}
      />
    );
  }

  // Course Viewer
  if (viewState === ViewState.COURSE_VIEWER && selectedEnrollment) {
    return (
      <EnrolledCourseViewer
        bookId={bookId}
        courseId={selectedEnrollment.courseId}
        user={user}
        enrollment={selectedEnrollment}
        onUpdateEnrollment={onUpdateEnrollment}
        onBack={() => {
          setSelectedEnrollment(null);
          setViewState(ViewState.DASHBOARD);
        }}
        diaryService={diaryService}
      />
    );
  }

  // Competency Report
  if (viewState === ViewState.COMPETENCY_REPORT && selectedEnrollment) {
    return (
      <CompetencyReport
        bookId={bookId}
        courseId={selectedEnrollment.courseId}
        userId={user?.uid}
        onBack={() => {
          setSelectedEnrollment(null);
          setViewState(ViewState.DASHBOARD);
        }}
        diaryService={diaryService}
      />
    );
  }

  // Dashboard View - My Enrolled Courses
  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="p-6 space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-indigo-600" />
              My Learning
            </h1>
            <p className="text-gray-600 mt-1">Track your course progress and achievements</p>
          </div>
          <button
            onClick={() => setViewState(ViewState.MARKETPLACE)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 shadow-lg"
            style={{ backgroundColor: customStyling.primaryColor }}
          >
            <Store className="w-5 h-5" /> Browse Marketplace
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={BookOpen} label="Enrolled" value={stats.enrolled} color="blue" />
          <StatCard icon={TrendingUp} label="In Progress" value={stats.inProgress} color="amber" />
          <StatCard icon={CheckCircle} label="Completed" value={stats.completed} color="green" />
          <StatCard icon={Trophy} label="Not Started" value={stats.notStarted} color="gray" />
        </div>

        {enrollments.length > 0 ? (
          <>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search your courses..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Enrolled Courses Grid */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Courses</h2>
              {filteredEnrollments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredEnrollments.map((enrollment) => (
                    <EnrolledCourseCard
                      key={enrollment.id}
                      enrollment={enrollment}
                      course={courses.find(c => c.id === enrollment.courseId)}
                      onClick={() => handleCourseClick(enrollment)}
                      onViewReport={() => handleViewReport(enrollment.courseId)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No courses found matching "{searchQuery}"
                </div>
              )}
            </div>
          </>
        ) : (
          // Empty State
          <EmptyState
            icon={BookOpen}
            title="No Enrolled Courses Yet"
            description="Start your learning journey by browsing our marketplace and enrolling in courses that interest you."
            action={
              <button
                onClick={() => setViewState(ViewState.MARKETPLACE)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
                style={{ backgroundColor: customStyling.primaryColor }}
              >
                <Store className="w-5 h-5" /> Explore Marketplace
              </button>
            }
          />
        )}

        {/* Quick Action Banner */}
        {enrollments.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Keep Learning!
                </h3>
                <p className="text-gray-600 text-sm">
                  Discover more courses to expand your knowledge
                </p>
              </div>
              <button
                onClick={() => setViewState(ViewState.MARKETPLACE)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                Browse More <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== COMPONENTS ====================

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    gray: 'bg-gray-50 text-gray-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-xs mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const EnrolledCourseCard = ({ enrollment, course, onClick, onViewReport }) => {
  if (!course) return null;
  
  const progress = enrollment.progress || 0;
  const isCompleted = progress >= 100;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
      <div onClick={onClick}>
        {/* Thumbnail */}
        <div className="aspect-video bg-gradient-to-br from-indigo-500 to-purple-600 relative">
          {course.thumbnail.url ? (
            <img src={course.thumbnail.url} alt={course.title} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-white opacity-50" />
            </div>
          )}
          {isCompleted && (
            <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Completed
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600">
            {course.title}
          </h3>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.instructor}</p>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>Progress</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 transition-all"
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: isCompleted ? '#10b981' : undefined 
                }}
              />
            </div>
          </div>

          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              {course.lessonCount || 0} lessons
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {Math.round((course.estimatedDuration || 0) / 60)}h
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex gap-2">
        <button
          onClick={onClick}
          className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-1 text-sm font-medium"
        >
          <Play className="w-4 h-4" /> {isCompleted ? 'Review' : 'Continue'}
        </button>
        {isCompleted && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewReport();
            }}
            className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center justify-center"
            title="View Report"
          >
            <BarChart3 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, description, action }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="bg-gray-100 p-6 rounded-full mb-4">
        <Icon className="w-12 h-12 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-center max-w-md mb-6">{description}</p>
      {action}
    </div>
  );
};

export default DiaryLearningView;